import { cx } from '@flowus/common/cx';
import type { PDFViewer } from 'pdfjs-dist/web/pdf_viewer';
import type { FC } from 'react';
import { useState } from 'react';
import { Icon } from 'src/common/components/icon';
import { ListView } from 'src/common/components/list-view';
import type { ListItem } from 'src/common/components/list-view/types';
import { ListItemType } from 'src/common/components/list-view/types';
import { useOpenModal } from 'src/common/components/next-modal';
import { emitter } from '@flowus/common/utils/emitter';

import { AnnotationColor } from '../type';
import { useAppTranslation } from '@flowus/common/i18n';

export const ColorMap = [
  { name: 'blue', value: AnnotationColor.BLUE },
  { name: 'green', value: AnnotationColor.GREEN },
  { name: 'yellow', value: AnnotationColor.YELLOW },
  { name: 'orange', value: AnnotationColor.ORANGE },
  { name: 'pink', value: AnnotationColor.PINK },
  { name: 'purple', value: AnnotationColor.PURPLE },
];

interface Props {
  uuid?: string;
  closeModal: () => void;
  pdfViewer: PDFViewer;
  color: string;
  setColor: React.Dispatch<React.SetStateAction<string>>;
}

export const ColorSetting: FC<Props> = ({ closeModal, color, setColor }) => {
  const { t } = useAppTranslation();

  const items = ColorMap.map((item) => {
    return {
      type: ListItemType.OPERATION,
      data: {
        title: t(`file_preview.pdf.buttons.color_${item.name}`),
        value: item.value,
        icon: (
          <span
            className="rounded-full w-[18px] h-[18px]"
            style={{ backgroundColor: item.value }}
          />
        ),
        arrow: <Icon name="IcCheck02" size="normal" />,
        hasArrow: color === item.value,
      },
    };
  });

  const handleClick = ({ data }: ListItem<any>) => {
    emitter.emit('pdfCursorColor', data.value);
    setColor(data.value);
    closeModal();
  };

  return (
    <ListView className="next-modal w-[160px] py-[5px]" items={items} onItemClick={handleClick} />
  );
};

interface ColorButtonProps {
  className?: string;
  pdfViewer: PDFViewer;
}
export const ColorButton: FC<ColorButtonProps> = ({ className, pdfViewer }) => {
  const openModal = useOpenModal();
  const [color, setColor] = useState(AnnotationColor.YELLOW);

  const openSetting = (event: React.MouseEvent) => {
    openModal.dropdown({
      popcorn: event.currentTarget,
      placement: 'bottom',
      offset: [0, 10],
      content: ({ onCloseModal }) => (
        <ColorSetting
          closeModal={onCloseModal}
          pdfViewer={pdfViewer}
          color={color}
          setColor={setColor}
        />
      ),
    });
  };

  return (
    <div className={cx('flex items-center animate-hover p-1', className)} onClick={openSetting}>
      <span className="rounded-full w-[18px] h-[18px]" style={{ backgroundColor: color }} />
      <Icon size="xxsmall" name={'IcArrowDown01'} className="ml-1" />
    </div>
  );
};
